import { Grid, Typography, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FullSaleTransactionDataFragment } from '../../../../api';
import Accordion from '../../../../components/Accordion';
import ContentWithLabel from '../../../../components/ContentWithLabel';
import FileList from '../../../../components/attachments/FileList';
import VehicleDiagram from '../../components/VehicleDiagram';

export type VehicleTabProps = {
    saleTransaction: FullSaleTransactionDataFragment;
};

const VehicleTab = ({ saleTransaction }: VehicleTabProps) => {
    const { t } = useTranslation(['saleTransactionsPage', 'common']);
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const useCardDesignForAccordion = !isSmall;

    return (
        <Grid spacing={2} container>
            <Grid xs={12} item>
                <Accordion
                    title={t('saleTransactionsPage:accordionTitle.vehicleDetails')}
                    useCardDesign={useCardDesignForAccordion}
                    defaultExpanded
                >
                    <Grid spacing={1} container>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.accessCode}
                                label={t('saleTransactionsPage:vehicle.accessCode')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.number}
                                label={t('saleTransactionsPage:vehicle.vehicleNo')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.beExported ? t('common:yes') : t('common:no')}
                                label={t('saleTransactionsPage:vehicle.vehicleToBeExported')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={t('common:formats.shortDate', {
                                    date: new Date(saleTransaction.vehicle.intendedDeregistrationDate),
                                })}
                                label={t('saleTransactionsPage:vehicle.intendedDeregistrationDate')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.make}
                                label={t('saleTransactionsPage:vehicle.vehicleMake')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.model}
                                label={t('saleTransactionsPage:vehicle.vehicleModel')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.primaryColour}
                                label={t('saleTransactionsPage:vehicle.primaryColour')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.secondaryColour}
                                label={t('saleTransactionsPage:vehicle.secondaryColour')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.manufacturingYear.toString()}
                                label={t('saleTransactionsPage:vehicle.manufacturingYear')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.engineNumber}
                                label={t('saleTransactionsPage:vehicle.engineNo')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.chassisNumber}
                                label={t('saleTransactionsPage:vehicle.chassisNo')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.maximumPowerOutput}
                                label={t('saleTransactionsPage:vehicle.maxPowerOutput')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={`${t(
                                    'common:currency'
                                )}${saleTransaction.vehicle.openMarketValue.toLocaleString()}`}
                                label={t('saleTransactionsPage:vehicle.openMarketValue')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={t('common:formats.shortDate', {
                                    date: new Date(saleTransaction.vehicle.originalRegistrationDate),
                                })}
                                label={t('saleTransactionsPage:vehicle.originalRegistrationDate')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={t('common:formats.shortDate', {
                                    date: new Date(saleTransaction.vehicle.firstRegistrationDate),
                                })}
                                label={t('saleTransactionsPage:vehicle.firstRegistrationDate')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.transferCount.toString()}
                                label={t('saleTransactionsPage:vehicle.transferCount')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={`${t(
                                    'common:currency'
                                )}${saleTransaction.vehicle.actualARFPaid.toLocaleString()}`}
                                label={t('saleTransactionsPage:vehicle.actualArfPaid')}
                            />
                        </Grid>
                        {(saleTransaction.vehicle.opcCashRebateEligibility ||
                            saleTransaction.vehicle.opcCashRebateEligibilityExpiryDate ||
                            saleTransaction.vehicle.opcCashRebateAmount) && (
                            <>
                                <Grid xs={12} item>
                                    <ContentWithLabel
                                        content={saleTransaction.vehicle.opcCashRebateEligibility}
                                        label={t('saleTransactionsPage:vehicle.opcCashRebateEligibility')}
                                    />
                                </Grid>
                                <Grid xs={12} item>
                                    <ContentWithLabel
                                        content={
                                            saleTransaction.vehicle.opcCashRebateEligibilityExpiryDate
                                                ? t('common:formats.shortDate', {
                                                      date: new Date(
                                                          saleTransaction.vehicle.opcCashRebateEligibilityExpiryDate
                                                      ),
                                                  })
                                                : t('common:blankValue')
                                        }
                                        label={t('saleTransactionsPage:vehicle.opcCashRebateEligibilityExpiryDate')}
                                    />
                                </Grid>
                                <Grid xs={12} item>
                                    <ContentWithLabel
                                        content={
                                            saleTransaction.vehicle.opcCashRebateAmount
                                                ? t('common:currency') +
                                                  saleTransaction.vehicle.opcCashRebateAmount.toLocaleString()
                                                : t('common:blankValue')
                                        }
                                        label={t('saleTransactionsPage:vehicle.opcCashRebateAmount')}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.parfEligibility ? t('common:yes') : t('common:no')}
                                label={t('saleTransactionsPage:vehicle.parfEligibility')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.vehicle.parfEligibilityDate
                                        ? t('common:formats.shortDate', {
                                              date: new Date(saleTransaction.vehicle.parfEligibilityDate),
                                          })
                                        : t('common:blankValue')
                                }
                                label={t('saleTransactionsPage:vehicle.parfEligibilityExpiryDate')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.vehicle.parfRebateAmount
                                        ? t('common:currency') +
                                          saleTransaction.vehicle.parfRebateAmount.toLocaleString()
                                        : t('common:blankValue')
                                }
                                label={t('saleTransactionsPage:vehicle.parfRebateValue')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={t('common:formats.shortDate', {
                                    date: new Date(saleTransaction.vehicle.coeExpiryDate),
                                })}
                                label={t('saleTransactionsPage:vehicle.coeExpiryDate')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.coeCategory}
                                label={t('saleTransactionsPage:vehicle.coeCategory')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.coePeriodYear.toString()}
                                label={t('saleTransactionsPage:vehicle.coePeriod')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.vehicle.qpPaid
                                        ? t('common:currency') + saleTransaction.vehicle.qpPaid.toLocaleString()
                                        : t('common:blankValue')
                                }
                                label={t('saleTransactionsPage:vehicle.qpPaid')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.vehicle.pqpPaid
                                        ? t('common:currency') + saleTransaction.vehicle.pqpPaid.toLocaleString()
                                        : t('common:blankValue')
                                }
                                label={t('saleTransactionsPage:vehicle.pqpPaid')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.vehicle.coeRebateAmount
                                        ? t('common:currency') +
                                          saleTransaction.vehicle.coeRebateAmount.toLocaleString()
                                        : t('common:blankValue')
                                }
                                label={t('saleTransactionsPage:vehicle.coeRebateAmount')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.vehicle.totalRebateAmount
                                        ? t('common:currency') +
                                          saleTransaction.vehicle.totalRebateAmount.toLocaleString()
                                        : t('common:blankValue')
                                }
                                label={t('saleTransactionsPage:vehicle.totalRebateAmount')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.ltaMessage}
                                label={t('saleTransactionsPage:vehicle.ltaMessage')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.mileage.toLocaleString()}
                                label={t('saleTransactionsPage:vehicle.mileage')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.vehicle.importMethod}
                                label={t('saleTransactionsPage:vehicle.importMethod')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.vehicle.setOfKeys
                                        ? saleTransaction.vehicle.setOfKeys.toString()
                                        : t('common:blankValue')
                                }
                                label={t('saleTransactionsPage:vehicle.setOfKeys')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.vehicle.ownerHandbookCollected ? t('common:yes') : t('common:no')
                                }
                                label={t('saleTransactionsPage:vehicle.ownerHandbookCollected')}
                            />
                        </Grid>
                    </Grid>
                </Accordion>
                <Accordion
                    title={t('saleTransactionsPage:accordionTitle.uploadPhotos')}
                    useCardDesign={useCardDesignForAccordion}
                    defaultExpanded
                >
                    <Grid spacing={1} container>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.photos.length > 0 ? (
                                        <FileList files={saleTransaction.photos} />
                                    ) : (
                                        <Typography>{t('saleTransactionsPage:noPhotos')}</Typography>
                                    )
                                }
                                display="block"
                                label={t('saleTransactionsPage:photos')}
                                mt={2}
                            />
                        </Grid>
                    </Grid>
                </Accordion>
                <Accordion
                    title={t('saleTransactionsPage:accordionTitle.vehicleCondition')}
                    defaultExpanded
                    useCardDesign
                >
                    <VehicleDiagram comments={saleTransaction.vehicleDiagramComments} isEditable={false} />
                </Accordion>
            </Grid>
        </Grid>
    );
};

export default VehicleTab;
